import type { EscalationRule, EscalationRuleStep } from '@/types/escalationRule';

function money(value: number) {
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function readNumber(value: number | string | null | undefined) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function readSteps(value: EscalationRule['steps']): EscalationRuleStep[] {
  if (!Array.isArray(value)) return [];
  const out: EscalationRuleStep[] = [];
  for (const step of value) {
    if (!step || typeof step !== 'object') continue;
    const row = step as Record<string, unknown>;
    const year = Number(row.year);
    if (!Number.isInteger(year) || year < 2) continue;
    if (row.kind === 'PERCENT') {
      const percent = Number(row.percent);
      if (Number.isFinite(percent)) out.push({ year, kind: 'PERCENT', percent });
    } else {
      const amount = Number(row.amount);
      if (Number.isFinite(amount)) out.push({ year, kind: 'AMOUNT', amount });
    }
  }
  return out.sort((a, b) => a.year - b.year);
}

export function summarizeEscalationRule(rule: Pick<EscalationRule, 'type' | 'value' | 'steps'>) {
  switch (rule.type) {
    case 'FIXED_PERCENT':
      return `+${readNumber(rule.value).toLocaleString()}% each year on the anniversary`;
    case 'FIXED_AMOUNT':
      return `+${money(readNumber(rule.value))} each year on the anniversary`;
    case 'CPI_INDEXED':
      return 'CPI-indexed (annual value entered manually)';
    case 'SCHEDULED_STEPS': {
      const steps = readSteps(rule.steps);
      return steps.length
        ? `Steps: ${steps
            .map((step) =>
              step.kind === 'PERCENT'
                ? `Yr ${step.year} +${step.percent}%`
                : `Yr ${step.year} ${money(step.amount ?? 0)}`,
            )
            .join(', ')}`
        : 'Scheduled steps';
    }
    case 'NONE':
    default:
      return 'Flat - no automatic increase';
  }
}
